import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import getClassName from 'src/common/utils/getClassName';
import dateTimeDurationSelector from 'src/inbox/selectors/dateTimeDuration/dateTimeDuration';
import { ImmutableTrip } from '../../../../types/trips/Trips';
import { GUESTS_FIELD } from '../../../constants';
import { formatGroupSize } from '../../../../common/helpers';
import { getUserBoats, getOffer } from '../../../../common/utils/reduxStoreSelectors';
import { getSelectedBoat, userIsRenter } from '../../../helpers';
import useCaptainData from '../../../../common/hooks/useCaptainData';
import Avatar from '../../../../common/components/Avatar';
import { totalGuests } from '../../../../common/components/GroupSizeDetails';
import Card from '../../presentation/Card';
import { offerGuestSelector } from '../GroupSize/selector';
import s from './BookingInfoCard.module.scss';

const TIME_FORMAT = 'h:mm aa';
const START_DATE_FORMAT = 'EEEE, MMMM dd, u';

type Props = {
  classNameModifier?: string;
  trip: ImmutableTrip;
};

const BookingInfoCard: FC<Props> = ({ classNameModifier = '', trip }) => {
  const offer = useSelector(getOffer);
  const userBoats = useSelector(getUserBoats);
  const otherUser = useMemo(() => trip.get(userIsRenter(trip) ? 'owner' : 'renter'), [trip]);

  const {
    offerTrip: {
      endDateTimeMoment, startDateTimeMoment, durationAsWords,
    },
  } = useSelector(dateTimeDurationSelector(trip.get('pk')));

  const { guests } = offerGuestSelector({ offer, trip, userBoats });
  const formattedGroupSize = formatGroupSize(totalGuests(guests), GUESTS_FIELD);

  const boat = getSelectedBoat(offer, trip, userBoats).toJS();

  const { captainOptionHeading } = useCaptainData(trip, offer);

  return (
    <Card>
      <h3 className={s.title}>Your booking with {otherUser.get('first_name')}</h3>
      <div className={s.wrapper}>
        <div className={s.avatars}>
          <Avatar
            classNameModifier="bookingInfo user"
            size="large"
            user={otherUser.toJS()}
          />
          <Avatar
            isBoat
            classNameModifier="bookingInfo boat"
            size="large"
            user={{
              ...boat,
              has_photo: !!boat?.photo_url,
              first_name: boat?.headline,
            }}
          />
        </div>
        <ul className={s.content}>
          <li className={getClassName(s, 'date', classNameModifier)}>
            {format(startDateTimeMoment.toDate(), START_DATE_FORMAT)}
          </li>
          <li>
            {`${format(startDateTimeMoment.toDate(), TIME_FORMAT)} - ${format(endDateTimeMoment.toDate(), TIME_FORMAT)}`}
          </li>
          <li>{durationAsWords}</li>
          <li>
            {formattedGroupSize} • {captainOptionHeading.shortLabel}
          </li>
        </ul>
      </div>
    </Card>
  );
};

export default BookingInfoCard;
